<script setup lang="ts">
import { onMounted, ref } from 'vue';
type ManagementRole = 'admin' | 'concierge' | 'supervisor';
type Contact = {
  key: string;
  role: 'provider' | 'owner' | ManagementRole;
  label: string;
  email: string;
  topics: string[];
  active: boolean;
  scoped: boolean;
};
type Message = {
  id: string;
  title: string;
  reference: string;
  to: string;
  status: string;
  createdAt: string;
};
type Settings = {
  revision: number;
  enabled: boolean;
  adminEmail: string;
  sender: string | null;
  transportConfigured: boolean;
  configurationInvalid?: boolean;
  contacts: Contact[];
  messages: Message[];
};
const data = ref<Settings | null>(null),
  busy = ref(false),
  error = ref(''),
  success = ref('');
const selected = ref<Contact | null>(null),
  active = ref(false);
const managementRoles: Record<ManagementRole, string> = {
  admin: 'Administration',
  concierge: 'Conciergerie',
  supervisor: 'Supervision des préparateurs',
};
const topics: Record<string, string> = {
  orders: 'Nouvelles commandes',
  sales: 'Ventes encaissées',
  interventions: 'Demandes d’intervention',
};
const management = ref<{
  key: string | null;
  role: ManagementRole;
  email: string;
  topics: string[];
} | null>(null);
function editManagement(contact?: Contact) {
  selected.value = null;
  management.value = contact
    ? {
        key: contact.key,
        role: contact.role as ManagementRole,
        email: contact.email,
        topics: [...contact.topics],
      }
    : { key: null, role: 'admin', email: '', topics: ['orders', 'sales', 'interventions'] };
  error.value = '';
  success.value = '';
}
const labels: Record<string, string> = {
  PENDING: 'En attente',
  SENDING: 'En cours d’envoi',
  ACCEPTED: 'Accepté par le service mail',
  FAILED: 'Refusé par le service mail',
  UNCERTAIN: 'Envoi incertain · à vérifier',
  CANCELLED: 'Envoi annulé',
};
async function load() {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/email-notifications', {
      credentials: 'include',
    });
    if (!response.ok) throw new Error();
    data.value = await response.json();
    active.value = Boolean(data.value?.enabled && data.value?.transportConfigured);
    selected.value = null;
    management.value = null;
  } catch {
    error.value = 'Impossible de charger les contacts e-mail. Réessayez.';
  } finally {
    busy.value = false;
  }
}
async function save(payload: Record<string, unknown>) {
  if (!data.value || busy.value) return;
  busy.value = true;
  error.value = '';
  success.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/email-notifications', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, revision: data.value.revision }),
    });
    const result = await response.json();
    if (!response.ok) {
      const errors: Record<string, string> = {
        EMAIL_SETTINGS_STALE:
          'Les réglages ont changé. Actualisez avant d’enregistrer à nouveau.',
        EMAIL_INPUT_INVALID: 'Vérifiez l’adresse e-mail saisie.',
        EMAIL_TRANSPORT_REQUIRED: 'L’envoi Gmail doit être configuré avant l’activation.',
        EMAIL_PROVIDER_SCOPE_REQUIRED:
          'Activez le compte et affectez son périmètre avant d’ajouter son adresse.',
        EMAIL_CONTACT_NOT_FOUND: 'Ce contact n’existe plus. Actualisez la liste.',
      };
      error.value =
        errors[result.error] ??
        'Enregistrement non confirmé. Actualisez pour vérifier avant de réessayer.';
      return;
    }
    data.value = result;
    active.value = Boolean(result.enabled && result.transportConfigured);
    selected.value = null;
    management.value = null;
    success.value = 'Enregistré.';
  } catch {
    error.value = 'Enregistrement non confirmé. Actualisez pour vérifier avant de réessayer.';
  } finally {
    busy.value = false;
  }
}
function edit(contact: Contact) {
  management.value = null;
  selected.value = { ...contact, topics: [...contact.topics] };
  success.value = '';
  error.value = '';
}
onMounted(load);
</script>
<template>
  <section class="email-contacts" aria-labelledby="email-title">
    <h2 id="email-title">E-mails et notifications</h2>
    <p>
      Choisissez les destinataires et les événements à signaler. Les prestataires reçoivent les
      consignes de leur périmètre, sans prix. Les notifications propriétaires sont facultatives.
    </p>
    <button type="button" class="secondary" :disabled="busy" @click="load">
      Actualiser les contacts
    </button>
    <p v-if="error" role="alert">{{ error }}</p>
    <p v-if="success" role="status">{{ success }}</p>
    <p v-if="busy" role="status">Traitement en cours…</p>
    <template v-if="data">
      <p class="mail-status" :class="{ enabled: active }">
        {{ active ? 'Notifications e-mail activées' : 'Notifications e-mail désactivées' }}
      </p>
      <p v-if="data.configurationInvalid" role="alert">
        La configuration de messagerie doit être vérifiée. Les autres fonctions restent
        disponibles.
      </p>
      <p v-if="!data.transportConfigured">
        L’envoi Gmail n’est pas configuré. Vous pouvez déjà enregistrer les contacts. Aucun
        e-mail ne sera envoyé.
      </p>
      <details>
        <summary>Équipe de gestion et envoi</summary>
        <form
          @submit.prevent="
            save({ kind: 'settings', adminEmail: data!.adminEmail, enabled: data!.enabled })
          "
        >
          <label for="admin-notification-email">E-mail principal de l’administration</label>
          <input
            id="admin-notification-email"
            v-model="data.adminEmail"
            type="email"
            maxlength="254"
            autocomplete="email"
          />
          <p v-if="data.sender">Expéditeur : {{ data.sender }}</p>
          <label class="check"
            ><input
              v-model="data.enabled"
              type="checkbox"
              :disabled="!data.transportConfigured && !data.enabled"
            />
            <span>Activer les notifications des nouveaux événements</span></label
          >
          <p>
            Les anciennes ventes et commandes ne seront pas renvoyées. Désactiver l’envoi annule
            les e-mails encore en attente.
          </p>
          <button type="submit" class="save" :disabled="busy">Enregistrer les réglages</button>
        </form>
        <h3>Autres destinataires de gestion</h3>
        <p>
          Ajoutez une ou plusieurs adresses pour l’administration, la conciergerie ou la
          supervision des préparateurs. Ces destinataires suivent tous les logements. Recevoir
          un e-mail ne crée pas de compte et ne change pas les droits de connexion.
        </p>
        <article
          v-for="contact in data.contacts.filter((c) => c.role in managementRoles)"
          :key="contact.key"
          class="contact"
        >
          <strong>{{ managementRoles[contact.role as ManagementRole] }}</strong>
          <p>{{ contact.email }}</p>
          <p>
            {{
              contact.topics.length
                ? contact.topics.map((topic) => topics[topic]).join(' · ')
                : 'Notifications suspendues'
            }}
          </p>
          <div class="actions">
            <button
              type="button"
              class="secondary"
              :disabled="busy"
              @click="editManagement(contact)"
            >
              Modifier
            </button>
            <button
              type="button"
              class="remove"
              :disabled="busy"
              @click="save({ kind: 'management-remove', key: contact.key })"
            >
              Retirer ce destinataire
            </button>
          </div>
        </article>
        <button
          v-if="!management"
          type="button"
          class="secondary"
          :disabled="busy"
          @click="editManagement()"
        >
          Ajouter un destinataire de gestion
        </button>
        <form
          v-else
          class="management-editor"
          @submit.prevent="save({ kind: 'management', ...management })"
        >
          <h3>{{ management.key ? 'Modifier le destinataire' : 'Nouveau destinataire' }}</h3>
          <fieldset>
            <legend>Rôle</legend>
            <label v-for="(label, role) in managementRoles" :key="role" class="check">
              <input
                v-model="management.role"
                type="radio"
                name="management-role"
                :value="role"
              />
              <span>{{ label }}</span>
            </label>
          </fieldset>
          <label for="management-email">Adresse e-mail</label>
          <input
            id="management-email"
            v-model="management.email"
            type="email"
            maxlength="254"
            autocomplete="email"
            required
          />
          <fieldset>
            <legend>Notifications à recevoir</legend>
            <label v-for="(label, topic) in topics" :key="topic" class="check">
              <input v-model="management.topics" type="checkbox" :value="topic" />
              <span>{{ label }}</span>
            </label>
          </fieldset>
          <p>
            Une même adresse reçoit un seul e-mail par événement, même si elle figure plusieurs
            fois.
          </p>
          <div class="actions">
            <button type="submit" class="save" :disabled="busy">
              Enregistrer le destinataire
            </button>
            <button type="button" class="secondary" :disabled="busy" @click="management = null">
              Annuler
            </button>
          </div>
        </form>
      </details>
      <details v-for="role in ['provider', 'owner'] as const" :key="role">
        <summary>
          {{ role === 'provider' ? 'Prestataires' : 'Propriétaires (facultatif)' }}
        </summary>
        <p v-if="role === 'owner'">
          Aucun envoi sans adresse renseignée et notification choisie. Vous pouvez laisser cette
          rubrique vide.
        </p>
        <p v-if="!data.contacts.some((c) => c.role === role)">
          Aucun {{ role === 'provider' ? 'prestataire' : 'propriétaire' }} enregistré.
        </p>
        <article
          v-for="contact in data.contacts.filter((c) => c.role === role)"
          :key="contact.key"
          class="contact"
        >
          <strong>{{ contact.label }}</strong>
          <p>
            {{
              contact.email ||
              (role === 'owner'
                ? 'Aucune notification propriétaire'
                : 'Aucune adresse renseignée')
            }}
          </p>
          <p v-if="!contact.active || !contact.scoped">
            Compte inactif ou sans périmètre : aucun envoi.
          </p>
          <form
            v-if="selected?.key === contact.key"
            @submit.prevent="
              save({
                kind: 'contact',
                key: selected!.key,
                email: selected!.email,
                topics: selected!.topics,
              })
            "
          >
            <label :for="'email-' + contact.key">Adresse e-mail de {{ contact.label }}</label>
            <input
              :id="'email-' + contact.key"
              v-model="selected.email"
              type="email"
              maxlength="254"
              autocomplete="email"
            />
            <label class="check"
              ><input v-model="selected.topics" type="checkbox" value="orders" /><span
                >Nouvelles commandes</span
              ></label
            >
            <label class="check"
              ><input v-model="selected.topics" type="checkbox" value="sales" /><span
                >Ventes encaissées</span
              ></label
            >
            <p>
              Effacez l’adresse ou décochez les deux notifications pour arrêter les envois à ce
              contact.
            </p>
            <div class="actions">
              <button class="save" type="submit" :disabled="busy">Enregistrer</button
              ><button
                class="secondary"
                type="button"
                :disabled="busy"
                @click="selected = null"
              >
                Annuler
              </button>
            </div>
          </form>
          <button
            v-else
            type="button"
            class="secondary"
            :disabled="busy"
            @click="edit(contact)"
          >
            {{ contact.email ? 'Modifier l’e-mail' : 'Ajouter un e-mail' }}
          </button>
        </article>
      </details>
      <details>
        <summary>Derniers envois ({{ data.messages.length }})</summary>
        <p>
          « Accepté » signifie que le service mail a pris en charge le message, sans garantir sa
          lecture. Les envois incertains ne sont pas relancés automatiquement.
        </p>
        <p v-if="!data.messages.length">Aucun e-mail préparé.</p>
        <article v-for="message in data.messages" :key="message.id" class="contact">
          <strong>{{ message.title }}</strong>
          <p>{{ message.to }}</p>
          <p>{{ labels[message.status] || message.status }}</p>
          <small
            >{{ new Date(message.createdAt).toLocaleString('fr-FR') }} ·
            {{ message.reference }}</small
          >
        </article>
      </details>
    </template>
  </section>
</template>
<style scoped>
.email-contacts {
  padding: 1rem;
  border: 1px solid #d6dce4;
  border-radius: 1rem;
  background: #fff;
  overflow-wrap: anywhere;
  margin-block: 1rem;
  color: #132b40;
  min-width: 0;
}
h2 {
  font-size: 1.3rem;
}
p {
  line-height: 1.5;
}
details {
  border: 1px solid #cbd4df;
  border-radius: 0.75rem;
  margin-block: 1rem;
  padding: 1rem;
  min-width: 0;
}
summary {
  font-weight: 750;
  cursor: pointer;
  min-height: 44px;
  display: list-item;
}
form {
  display: grid;
  gap: 0.8rem;
  min-width: 0;
}
input[type='email'] {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  min-height: 48px;
  padding: 0.65rem;
  border: 1px solid #8391a1;
  border-radius: 0.6rem;
  font-size: 1rem;
}
.check {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  line-height: 1.4;
}
.check input {
  width: 22px;
  height: 22px;
  min-width: 22px;
  flex: 0 0 22px;
  padding: 0;
  margin: 0;
}
.check span {
  min-width: 0;
  flex: 1;
}
.contact {
  padding-block: 1rem;
  border-top: 1px solid #d6dce4;
  min-width: 0;
}
.actions {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}
button {
  min-height: 44px;
  border-radius: 0.6rem;
  padding: 0.7rem 1rem;
  font-weight: 700;
  max-width: 100%;
}
.save {
  background: #21613e;
  color: white;
  border: 1px solid #21613e;
}
.secondary {
  background: #edf1f6;
  color: #152e4a;
  border: 1px solid #74859a;
}
fieldset {
  min-width: 0;
  border: 1px solid #cbd4df;
  border-radius: 0.6rem;
  padding: 0.8rem;
  display: grid;
  gap: 0.75rem;
}
legend {
  font-weight: 700;
}
.management-editor {
  margin-top: 1rem;
}
.remove {
  background: #a5232e;
  color: white;
  border: 1px solid #a5232e;
}
button:disabled {
  opacity: 0.65;
}
.mail-status {
  border-radius: 0.6rem;
  padding: 0.7rem;
  background: #fff1bf;
}
.mail-status.enabled {
  background: #d9f1df;
}
[role='alert'] {
  color: #8e1222;
}
[role='status'] {
  color: #21613e;
}
</style>
